import { useCallback, useEffect, useState } from "react";
import { Layout } from "@/components/Layout";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Truck, Package, CheckCircle2, Clock, Loader2, MapPin } from "lucide-react";
import { toast } from "sonner";

const DELIVERY_STEPS = ["Assigned", "Picked up", "In transit", "Out for delivery", "Delivered"];

const fmt = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d) ? "—" : d.toLocaleString(undefined, { month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" });
};

function UpdateRow({ order, onDone }) {
  const [status, setStatus] = useState("");
  const [note, setNote] = useState("");
  const [location, setLocation] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!status) return toast.error("Select a status");
    setBusy(true);
    try {
      await api.post(`/delivery/orders/${order.order_id}/status`, { status, note, location });
      toast.success(`Marked as ${status}`);
      setNote(""); setLocation(""); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to update"); }
    setBusy(false);
  };

  return (
    <div className="flex flex-col sm:flex-row gap-2 mt-2">
      <Select value={status} onValueChange={setStatus}>
        <SelectTrigger data-testid={`delivery-status-select-${order.order_id}`} className="sm:w-44"><SelectValue placeholder="Update status" /></SelectTrigger>
        <SelectContent>{DELIVERY_STEPS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
      </Select>
      <Input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Current location (optional)" className="sm:w-48" />
      <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (optional)" className="flex-1" />
      <Button data-testid={`delivery-update-btn-${order.order_id}`} onClick={submit} disabled={busy} className="rounded-full">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Update"}
      </Button>
    </div>
  );
}

export default function DeliveryDashboard() {
  const [data, setData] = useState(null);

  const load = useCallback(() => {
    api.get("/delivery/dashboard").then((r) => setData(r.data)).catch(() => setData({ delivery_status: "none", counts: {}, orders: [] }));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!data) return <Layout><div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div></Layout>;

  if (data.delivery_status !== "approved") {
    const label = { pending: "awaiting admin approval", rejected: "was not approved", suspended: "is suspended", none: "is not set up" }[data.delivery_status] || "is pending";
    return (
      <Layout hideAd>
        <div className="max-w-lg mx-auto px-4 py-20 text-center">
          <Truck className="w-12 h-12 text-primary mx-auto mb-4" />
          <h1 className="font-display font-bold text-2xl mb-2">Delivery Agent</h1>
          <p className="text-muted-foreground">Your delivery agent account {label}. You'll be able to see assigned deliveries here once approved by an admin.</p>
          <Badge className="mt-4 capitalize" variant="outline" data-testid="delivery-status-badge">{data.delivery_status}</Badge>
        </div>
      </Layout>
    );
  }

  const c = data.counts || {};
  const cards = [
    { icon: Package, label: "Assigned", value: c.total || 0, color: "text-primary" },
    { icon: Clock, label: "In progress", value: c.in_progress || 0, color: "text-amber-600" },
    { icon: CheckCircle2, label: "Delivered", value: c.delivered || 0, color: "text-green-600" },
  ];

  return (
    <Layout hideAd>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="font-display font-bold text-3xl mb-1">Delivery Dashboard</h1>
        <p className="text-muted-foreground mb-6">Manage your assigned deliveries and update their status.</p>

        <div className="grid grid-cols-3 gap-4 mb-6">
          {cards.map((k) => (
            <Card key={k.label} className="p-4" data-testid={`delivery-stat-${k.label.toLowerCase().replace(/\s/g, "-")}`}>
              <k.icon className={`w-5 h-5 ${k.color} mb-2`} />
              <p className="text-2xl font-black font-display">{k.value}</p>
              <p className="text-xs text-muted-foreground">{k.label}</p>
            </Card>
          ))}
        </div>

        <Card>
          <Table>
            <TableHeader><TableRow><TableHead>Order</TableHead><TableHead>Total</TableHead><TableHead>Status</TableHead><TableHead>Last update</TableHead></TableRow></TableHeader>
            <TableBody>
              {data.orders.map((o) => (
                <TableRow key={o.order_id} data-testid={`delivery-order-${o.order_id}`}>
                  <TableCell colSpan={4} className="align-top">
                    <div className="flex flex-wrap items-center gap-3 justify-between">
                      <div>
                        <p className="font-mono text-xs">#{o.order_id.slice(-8)}</p>
                        <p className="text-sm font-semibold">€{(o.total || 0).toFixed(2)}</p>
                        {o.shipping_address && <p className="text-xs text-muted-foreground flex items-center gap-1"><MapPin className="w-3 h-3" />{typeof o.shipping_address === "string" ? o.shipping_address : (o.shipping_address.city || o.shipping_address.line1 || "")}</p>}
                      </div>
                      <Badge className="capitalize" variant="outline">{(o.delivery_status || "assigned").replace("_", " ")}</Badge>
                    </div>
                    <UpdateRow order={o} onDone={load} />
                  </TableCell>
                </TableRow>
              ))}
              {data.orders.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-8">No deliveries assigned to you yet.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </Card>
      </div>
    </Layout>
  );
}
